import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  PermissionAction,
  SurveyFilter as SurveyFilterModel,
  SurveyListDto,
  SurveyListFilter,
  SurveyListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { SurveyFilter } from "../SurveyFilter";
import { surveyPageRoute } from "../SurveyPage/SurveyPage";
import { SurveyTable } from "./SurveyTable";

const routeName = "surveys";

export const surveyListPageRoute: PageRoute = {
  handle: "Surveys",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const SurveyListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Form;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<SurveyListFilter>({
    sortObjects: [{ property: pn<SurveyListDto>("id"), isDesc: true }],
  });
  const [initListFilter] = useState<SurveyListFilter>(listFilter);
  const [listItems, setListItems] = useState<SurveyListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<SurveyListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, loadList, applyPage, applyPageSize, applySortMethod, applySort, applySearch } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.surveys.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.surveys.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    fetchResource();
    loadList(listFilter);
  }, []);

  const fetchResource = () => {
    ApiService.surveys.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<SurveyListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <SurveyFilter
        filter={listFilter.surveyFilter || ({} as SurveyFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.surveyFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{surveyListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() => navigate(surveyPageRoute.getPath({ id: 0, crudMode: "edit" }))}
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <SurveyTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              surveys={listItems}
              resource={resource}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
